import { useCallback, useMemo } from 'react';
import { useLazyQuery, type QueryResult, type WatchQueryFetchPolicy } from '@apollo/client';
import {
  GetInspectionTemplatesDocument,
  type GetInspectionTemplatesQuery,
  type GetInspectionTemplatesQueryVariables,
  SourceType,
} from 'graphql-query-contracts';
import type { InspectionTemplate } from 'inspection-types';

import { mapGraphQLTemplatesToDomain } from 'domains-inspection-client-mappers';
import { InvalidInspectionTemplatesBuildingIdError } from './errors/InvalidInspectionTemplatesBuildingIdError';

export { InvalidInspectionTemplatesBuildingIdError };

export type FetchInspectionTemplatesParams = {
  buildingId: string;
  entityType: string;
  sourceType: SourceType;
};

export type FetchInspectionTemplatesResult = Promise<
  QueryResult<GetInspectionTemplatesQuery, GetInspectionTemplatesQueryVariables>
>;

export type UseGetInspectionTemplatesOptions = {
  fetchPolicy?: WatchQueryFetchPolicy;
};

export type UseGetInspectionTemplatesResult = {
  templates: InspectionTemplate[];
  loading: boolean;
  error: Error | undefined;
  fetchInspectionTemplates: (
    params: FetchInspectionTemplatesParams
  ) => FetchInspectionTemplatesResult;
  called: boolean;
};

export function useGetInspectionTemplates(
  options?: UseGetInspectionTemplatesOptions
): UseGetInspectionTemplatesResult {
  const [fetchTemplates, { loading, data, error, called }] = useLazyQuery(
    GetInspectionTemplatesDocument,
    {
      fetchPolicy: options?.fetchPolicy ?? 'network-only',
    }
  );

  const fetchInspectionTemplates = useCallback(
    ({
      buildingId,
      entityType,
      sourceType,
    }: FetchInspectionTemplatesParams): FetchInspectionTemplatesResult => {
      if (!buildingId || buildingId.trim() === '') {
        throw new InvalidInspectionTemplatesBuildingIdError();
      }

      return fetchTemplates({
        variables: {
          buildingId: {
            sourceEntityId: buildingId,
            sourceEntityType: entityType,
            sourceType,
          },
        },
      });
    },
    [fetchTemplates]
  );

  const templates = useMemo(
    () =>
      data?.inspectionTemplates
        ? mapGraphQLTemplatesToDomain(data.inspectionTemplates)
        : [],
    [data]
  );

  return {
    templates,
    loading,
    error,
    fetchInspectionTemplates,
    called,
  };
}
